import {
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@infinitaslearning/pixel-design-system";
import { getAnalyticsTracker } from "analytics";
import { Player } from "components/Player";
import { TabButton } from "components/TabButton";
import { ASSETS_TYPES } from "constants/index";
import type { Asset } from "models/asset/Asset";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { DocumentsDownloadProps } from "./ExtraMaterial.model";
import {
  AssetsContainer,
  ButtonsContainer,
  CardTitle,
  MaterialContainer,
  StyleContainer,
  StyledCard,
  StyledIcon,
  StyledVideoIcon,
} from "./ExtraMaterial.styled";

export const ExtraMaterial: React.FC<DocumentsDownloadProps> = ({ assets }) => {
  const { t } = useTranslation("material");
  const [filter, setFilter] = useState("");
  const assetsToShow = filter ? assets?.filter((a) => a.type === filter) : assets;

  const [isVideoPlayerOpened, setIsVideoPlayerOpened] = useState(false);
  const [selectedVideoAsset, setSelectedVideoAsset] = useState<Asset | null>(null);
  const handleOpen = (asset: Asset) => {
    setSelectedVideoAsset(asset);
    setIsVideoPlayerOpened(true);
    getAnalyticsTracker()?.elementClicked({
      elementId: "open-video-player",
      module: "extra-material-list",
      type: asset.type,
      itemId: `extra-material-${asset.type}-${asset.id}`,
    });
  };
  const handleClose = () => setIsVideoPlayerOpened(false);

  const sendFileDownloadedEvent = (asset: Asset) => {
    getAnalyticsTracker()?.fileDownloaded({
      url: asset.content.content.href,
      name: asset.title,
      id: asset.id,
      source: "edify",
      extension: "pdf",
    });
  };

  const sendExitLinkEvent = (asset: Asset) => {
    getAnalyticsTracker()?.exitLinkClicked({
      url: asset.content.href || "",
      label: asset.title,
      module: "extra-material-list",
      itemId: `extra-material-${asset.type}-${asset.id}`,
    });
  };

  const sendAudioEvent = (asset: Asset) => {
    getAnalyticsTracker()?.elementClicked({
      elementId: "open-audio-player",
      module: "extra-material-list",
      type: asset.type,
      itemId: `extra-material-${asset.type}-${asset.id}`,
    });
  };

  const renderAsset = (asset: Asset) => {
    if (asset.type === ASSETS_TYPES.document) {
      return (
        <MaterialContainer
          key={asset.id}
          data-cy="asset-document"
          data-mdat="element-clicked"
          data-mdat-item-id={`extra-material-${asset.type}-${asset.id}`}
          data-mdat-module="extra-material-list"
          onClick={() => sendFileDownloadedEvent(asset)}
        >
          <StyledCard href={asset.content.content.href}>
            <StyledIcon className="icon-bundle" />
          </StyledCard>
          <Tooltip title={asset.title}>
            <CardTitle>{asset.title}</CardTitle>
          </Tooltip>
        </MaterialContainer>
      );
    }
    if (asset.type === ASSETS_TYPES.link) {
      const assetTitleLink = asset.content?.children?.find((elem) => elem.text)?.text as string;
      return (
        <MaterialContainer
          key={asset.id}
          data-cy="asset-link"
          data-mdat="element-clicked"
          data-mdat-item-id={`extra-material-${asset.type}-${asset.id}`}
          data-mdat-module="extra-material-list"
          onClick={() => sendExitLinkEvent(asset)}
        >
          <StyledCard href={asset.content.href} target="_blank">
            <StyledIcon className="icon-link" />
          </StyledCard>
          <Tooltip title={asset.title}>
            <CardTitle>{assetTitleLink || asset.title}</CardTitle>
          </Tooltip>
        </MaterialContainer>
      );
    }
    if (asset.type === ASSETS_TYPES.video) {
      return (
        <>
          <MaterialContainer
            key={asset.id}
            data-cy="video-player-item"
            data-mdat="element-clicked"
            data-mdat-item-id={`extra-material-${asset.type}-${asset.id}`}
            data-mdat-module="extra-material-list"
            onClick={() => handleOpen(asset)}
          >
            <StyledCard href={asset.content.href} target="_blank">
              <StyledVideoIcon className="icon-link" fontSize="large" />
            </StyledCard>
            <Tooltip title={asset.title}>
              <CardTitle>{asset.title}</CardTitle>
            </Tooltip>
          </MaterialContainer>
        </>
      );
    }
    if (asset.type === ASSETS_TYPES.audio) {
      return (
        <>
          <MaterialContainer
            key={asset.id}
            data-cy="audio-player-item"
            data-mdat="element-clicked"
            data-mdat-item-id={`extra-material-${asset.type}-${asset.id}`}
            data-mdat-module="extra-material-list"
            onClick={() => sendAudioEvent(asset)}
          >
            <StyledCard href={asset.content.href} target="_blank">
              <Player key={asset?.id} initialData={asset.content} />
            </StyledCard>
            <Tooltip title={asset.title}>
              <CardTitle>{asset.title}</CardTitle>
            </Tooltip>
          </MaterialContainer>
        </>
      );
    }
    return null;
  };
  return (
    <StyleContainer>
      <ButtonsContainer>
        {assets?.length > 0 && (
          <TabButton dataCy="tab-button-all" isActive={filter === ""} onClick={() => setFilter("")}>
            {t("additionals.all", "All")}
          </TabButton>
        )}
        {assets?.some((a) => a.type === ASSETS_TYPES.link) && (
          <TabButton
            dataCy="tab-button-links"
            isActive={filter === ASSETS_TYPES.link}
            onClick={() => setFilter(ASSETS_TYPES.link)}
          >
            {t("additionals.links", "Links")}
          </TabButton>
        )}
        {assets?.some((a) => a.type === ASSETS_TYPES.document) && (
          <TabButton
            dataCy="tab-button-documents"
            isActive={filter === ASSETS_TYPES.document}
            onClick={() => setFilter(ASSETS_TYPES.document)}
          >
            {t("additionals.documents", "Documents")}
          </TabButton>
        )}
        {assets?.some((a) => a.type === ASSETS_TYPES.video) && (
          <TabButton
            dataCy="tab-button-videos"
            isActive={filter === ASSETS_TYPES.video}
            onClick={() => setFilter(ASSETS_TYPES.video)}
          >
            {t("additionals.videos", "Videos")}
          </TabButton>
        )}
        {assets?.some((a) => a.type === ASSETS_TYPES.audio) && (
          <TabButton
            dataCy="tab-button-audio"
            isActive={filter === ASSETS_TYPES.audio}
            onClick={() => setFilter(ASSETS_TYPES.audio)}
          >
            {t("additionals.audio", "Audio")}
          </TabButton>
        )}
      </ButtonsContainer>
      <AssetsContainer>
        {assetsToShow?.map((a) => renderAsset(a))}
        <Dialog open={isVideoPlayerOpened} onClose={handleClose}>
          <DialogTitle sx={{ marginLeft: 1 }}>{selectedVideoAsset?.title}</DialogTitle>
          <DialogContent>
            <Player
              key={selectedVideoAsset?.id}
              dataCy="video-player-item"
              initialData={selectedVideoAsset?.content}
            />
          </DialogContent>
        </Dialog>
      </AssetsContainer>
    </StyleContainer>
  );
};
